/**
 * SessionCatalog — Mentor's session type management dashboard
 *
 * Lets a mentor create, edit, delete, and toggle their 1:1 session offerings.
 * Each session type shows a full pricing breakdown:
 *   Regular (slash) price  →  Offer price
 *   Platform commission 15% (JSDevBrains Digital Private Limited)
 *   Mentor net earning
 *
 * Routes:  /dashboard/mentor/services
 * API:     GET/POST/PUT/DELETE /v1/mentors/me/session-types
 */

import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { convertPrice } from '../data/mockData';
import { apiClient } from '../services';
import './SessionCatalog.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const COMMISSION_RATE   = 0.15;
const PLATFORM_COMPANY  = 'JSDevBrains Digital Private Limited';

const PLATFORM_OPTIONS = [
  { id: 'google_meet', label: 'Google Meet',       icon: '🟢', color: '#34a853' },
  { id: 'zoom',        label: 'Zoom',               icon: '🔵', color: '#2D8CFF' },
  { id: 'teams',       label: 'Microsoft Teams',    icon: '🟣', color: '#6264a7' },
  { id: 'whereby',     label: 'Whereby',             icon: '🟠', color: '#f47023' },
  { id: 'jitsi',       label: 'Jitsi Meet',          icon: '⚫', color: '#374151' },
];

const DURATION_OPTIONS = [
  { value: 30,  label: '30 min  (0.5 hr)'  },
  { value: 45,  label: '45 min'             },
  { value: 60,  label: '60 min  (1 hr)'     },
  { value: 90,  label: '90 min  (1.5 hr)'  },
  { value: 120, label: '120 min (2 hr)'    },
  { value: 180, label: '180 min (3 hr)'    },
];

const SESSION_TYPE_PRESETS = [
  { name: '1:1 Mentoring',    icon: '🎯', description: 'One-on-one focused session on any topic of your choice.' },
  { name: 'Code Review',      icon: '💻', description: 'Deep-dive code review with line-by-line feedback and improvement suggestions.' },
  { name: 'Mock Interview',   icon: '🎤', description: 'Simulated technical interview with real-time feedback, scoring, and improvement tips.' },
  { name: 'Career Roadmap',   icon: '🗺️', description: 'Strategic career planning session to map your path to your dream tech role.' },
  { name: 'Portfolio Review', icon: '🖼️', description: 'Full portfolio or resume review with actionable improvement feedback.' },
  { name: 'System Design',    icon: '🏗️', description: 'Deep-dive system design session for senior-level interview preparation.' },
  { name: 'Custom Session',   icon: '⚡', description: '' },
];

const EMPTY_FORM = {
  name:             '',
  customName:       '',
  description:      '',
  duration:         60,
  topics:           [],
  allowedPlatforms: ['google_meet', 'zoom', 'teams'],
  originalPriceINR: '',
  priceINR:         '',
  enabled:          true,
};

// Default mock session types shown first time
const MOCK_SESSION_TYPES = [
  {
    id: 'st_1',
    name: '1:1 Mentoring',
    icon: '🎯',
    description: 'One-on-one focused session on any topic — React, Node.js, system design, or career questions.',
    duration: 60,
    topics: ['React', 'Node.js', 'System Design', 'AWS', 'TypeScript'],
    allowedPlatforms: ['google_meet', 'zoom', 'teams'],
    originalPriceINR: 2000,
    priceINR: 1600,
    enabled: true,
  },
  {
    id: 'st_2',
    name: 'Code Review',
    icon: '💻',
    description: "Submit your repository or PR link before the session. I'll review your architecture, code quality, and security.",
    duration: 45,
    topics: ['Code Quality', 'Architecture', 'Best Practices', 'Security'],
    allowedPlatforms: ['google_meet', 'zoom'],
    originalPriceINR: 0,
    priceINR: 1280,
    enabled: true,
  },
  {
    id: 'st_3',
    name: 'Mock Interview',
    icon: '🎤',
    description: 'Simulated FAANG-style technical interview with real-time feedback on communication, approach, and correctness.',
    duration: 60,
    topics: ['LeetCode', 'System Design', 'Behavioral', 'FAANG Prep'],
    allowedPlatforms: ['google_meet', 'zoom', 'teams'],
    originalPriceINR: 2000,
    priceINR: 1600,
    enabled: true,
  },
  {
    id: 'st_4',
    name: 'Career Roadmap',
    icon: '🗺️',
    description: '30-min strategic session to map out your exact path from where you are to your dream tech role.',
    duration: 30,
    topics: ['Career Planning', 'Job Search', 'Resume', 'Interview Strategy'],
    allowedPlatforms: ['google_meet'],
    originalPriceINR: 1000,
    priceINR: 800,
    enabled: false,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function commissionBreakdown(priceINR) {
  const commission = Math.round(priceINR * COMMISSION_RATE);
  const earning    = priceINR - commission;
  return { commission, earning, rate: COMMISSION_RATE * 100 };
}

// ─── Topic tag input ───────────────────────────────────────────────────────────

function TopicInput({ topics, onChange }) {
  const [input, setInput] = useState('');

  const addTopic = (val) => {
    const trimmed = val.trim();
    if (trimmed && !topics.includes(trimmed)) onChange([...topics, trimmed]);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTopic(input);
    } else if (e.key === 'Backspace' && !input && topics.length > 0) {
      onChange(topics.slice(0, -1));
    }
  };

  return (
    <div className="scat-topic-input-wrap">
      {topics.map(t => (
        <span key={t} className="scat-topic-chip-edit">
          {t}
          <button type="button" onClick={() => onChange(topics.filter(x => x !== t))}>×</button>
        </span>
      ))}
      <input
        className="scat-topic-input"
        placeholder={topics.length === 0 ? 'Add topics (press Enter or comma to add)' : 'Add more…'}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addTopic(input); }}
      />
    </div>
  );
}

// ─── Live commission preview (shown inside the form) ──────────────────────────

function CommissionPreview({ priceINR, originalPriceINR, currency }) {
  const price = parseInt(priceINR) || 0;
  if (!price) return null;
  const { commission, earning, rate } = commissionBreakdown(price);
  const orig      = parseInt(originalPriceINR) || 0;
  const hasSlash  = orig > price;
  const discount  = hasSlash ? Math.round(((orig - price) / orig) * 100) : 0;

  return (
    <div className="scat-commission-preview">
      <div className="scat-cp-header">
        <span className="scat-cp-title">💰 Live Earnings Preview</span>
        {hasSlash && (
          <span className="scat-discount-pill">-{discount}% offer</span>
        )}
      </div>
      <div className="scat-cp-rows">
        {hasSlash && (
          <div className="scat-cp-row">
            <span className="scat-cp-lbl">Regular Price</span>
            <span className="scat-cp-slash">{convertPrice(orig, currency)}</span>
          </div>
        )}
        <div className="scat-cp-row">
          <span className="scat-cp-lbl">{hasSlash ? 'Offer Price' : 'Session Price'}</span>
          <span className="scat-cp-val">{convertPrice(price, currency)}</span>
        </div>

        <div className="scat-cp-separator" />

        <div className="scat-cp-row scat-cp-row-commission">
          <div>
            <div className="scat-cp-lbl">Platform Commission ({rate}%)</div>
            <div className="scat-cp-company">🏢 {PLATFORM_COMPANY}</div>
            <div className="scat-cp-hint">Platform & environment maintenance fees</div>
          </div>
          <span className="scat-cp-commission-val">−{convertPrice(commission, currency)}</span>
        </div>

        <div className="scat-cp-separator scat-cp-sep-bold" />

        <div className="scat-cp-row scat-cp-row-earning">
          <span className="scat-cp-earning-lbl">✅ Your Net Earning</span>
          <span className="scat-cp-earning-val">{convertPrice(earning, currency)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Session type card ────────────────────────────────────────────────────────

function SessionTypeCard({ st, onEdit, onDelete, onToggle, currency }) {
  const { commission, earning, rate } = commissionBreakdown(st.priceINR || 0);
  const hasSlash = !!(st.originalPriceINR && st.originalPriceINR > st.priceINR);
  const discount  = hasSlash ? Math.round(((st.originalPriceINR - st.priceINR) / st.originalPriceINR) * 100) : 0;
  const icon      = st.icon || SESSION_TYPE_PRESETS.find(p => p.name === st.name)?.icon || '⚡';

  return (
    <div className={`scat-card ${st.enabled ? '' : 'scat-card-disabled'}`}>

      {/* ── Card header ── */}
      <div className="scat-card-header">
        <div className="scat-card-title-row">
          <span className="scat-card-icon">{icon}</span>
          <div className="scat-card-name-wrap">
            <span className="scat-name">{st.name}</span>
            {hasSlash && <span className="scat-discount-badge">-{discount}% OFF</span>}
          </div>
          <div className="scat-card-actions">
            <button
              className={`scat-status-btn ${st.enabled ? 'scat-status-active' : 'scat-status-hidden'}`}
              onClick={() => onToggle(st.id || st._id)}
              title={st.enabled ? 'Click to hide from mentees' : 'Click to make visible'}
            >
              {st.enabled ? '● Active' : '○ Hidden'}
            </button>
            <button className="scat-btn-icon scat-btn-edit" onClick={() => onEdit(st)} title="Edit">✏️</button>
            <button className="scat-btn-icon scat-btn-delete" onClick={() => onDelete(st.id || st._id)} title="Delete">🗑</button>
          </div>
        </div>
      </div>

      {/* ── Meta row ── */}
      <div className="scat-meta-row">
        <span className="scat-meta-item">⏱ {st.duration} min</span>
        {st.allowedPlatforms?.length > 0 && (
          <span className="scat-meta-item scat-meta-platforms">
            {st.allowedPlatforms.map(pid => {
              const p = PLATFORM_OPTIONS.find(x => x.id === pid);
              return p ? <span key={pid} title={p.label} className="scat-plat-icon">{p.icon}</span> : null;
            })}
          </span>
        )}
      </div>

      {/* ── Description ── */}
      {st.description && <p className="scat-description">{st.description}</p>}

      {/* ── Topics ── */}
      {st.topics?.length > 0 && (
        <div className="scat-topics-row">
          {st.topics.slice(0, 5).map(t => (
            <span key={t} className="scat-topic-chip">{t}</span>
          ))}
          {st.topics.length > 5 && (
            <span className="scat-topic-chip scat-topic-more">+{st.topics.length - 5} more</span>
          )}
        </div>
      )}

      {/* ── Allowed platforms (labeled) ── */}
      {st.allowedPlatforms?.length > 0 && (
        <div className="scat-platforms-row">
          <span className="scat-platforms-label">📡 Allowed platforms:</span>
          {st.allowedPlatforms.map(pid => {
            const p = PLATFORM_OPTIONS.find(x => x.id === pid);
            return p ? (
              <span key={pid} className="scat-platform-badge">{p.icon} {p.label}</span>
            ) : null;
          })}
        </div>
      )}

      {/* ── Pricing breakdown box ── */}
      <div className="scat-pricing-box">
        <div className="scat-pricing-box-title">💰 Pricing Breakdown</div>
        <div className="scat-pricing-rows">

          {hasSlash && (
            <div className="scat-pricing-row">
              <span className="scat-pricing-lbl">Regular Price</span>
              <span className="scat-pricing-slash">{convertPrice(st.originalPriceINR, currency)}</span>
            </div>
          )}

          <div className="scat-pricing-row">
            <span className="scat-pricing-lbl">{hasSlash ? 'Offer Price' : 'Session Price'}</span>
            <span className="scat-pricing-main">{convertPrice(st.priceINR || 0, currency)}</span>
          </div>

          <div className="scat-pricing-separator" />

          <div className="scat-pricing-row scat-commission-row">
            <div className="scat-commission-label-wrap">
              <span className="scat-pricing-lbl">Platform Commission ({rate}%)</span>
              <div className="scat-commission-company">🏢 {PLATFORM_COMPANY}</div>
              <div className="scat-commission-sub-note">Platform &amp; environment maintenance fees</div>
            </div>
            <span className="scat-pricing-commission">−{convertPrice(commission, currency)}</span>
          </div>

          <div className="scat-pricing-separator scat-sep-bold" />

          <div className="scat-pricing-row scat-earning-row">
            <span className="scat-earning-lbl">✅ Your Net Earning</span>
            <span className="scat-pricing-earning">{convertPrice(earning, currency)}</span>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SessionCatalog() {
  const { currency }   = useContext(AppContext);
  const { user }       = useAuth();
  const isMock         = process.env.REACT_APP_MOCK !== 'false';
  const slug           = user?.mentorSlug || (user?.email || '').split('@')[0].toLowerCase();
  const storageKey     = `db_session_types_${slug}`;

  const [sessions, setSessions]   = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId,   setEditId]     = useState(null);
  const [form,     setForm]       = useState(EMPTY_FORM);
  const [saving,   setSaving]     = useState(false);
  const [saveMsg,  setSaveMsg]    = useState('');

  // ── Load ──────────────────────────────────────────────────
  useEffect(() => {
    if (isMock) {
      try {
        const raw = localStorage.getItem(storageKey);
        setSessions(raw ? JSON.parse(raw) : MOCK_SESSION_TYPES);
      } catch {
        setSessions(MOCK_SESSION_TYPES);
      }
      setLoading(false);
      return;
    }
    if (!sessionStorage.getItem('db_user')) { setLoading(false); return; }
    apiClient.get('/mentors/me/session-types')
      .then(res => setSessions(res.sessionTypes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // ── Persist to localStorage in mock mode ──────────────────
  const persist = (updated) => {
    setSessions(updated);
    if (isMock) localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Form open ─────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowForm(true);
    setSaveMsg('');
    // Scroll to top of form after render
    setTimeout(() => document.querySelector('.scat-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const openEdit = (st) => {
    const preset = SESSION_TYPE_PRESETS.find(p => p.name === st.name);
    setForm({
      name:             preset ? st.name : 'Custom Session',
      customName:       preset ? '' : st.name,
      description:      st.description || '',
      duration:         st.duration || 60,
      topics:           st.topics || [],
      allowedPlatforms: st.allowedPlatforms?.length ? st.allowedPlatforms : ['google_meet'],
      originalPriceINR: st.originalPriceINR ? String(st.originalPriceINR) : '',
      priceINR:         String(st.priceINR || ''),
      enabled:          st.enabled !== false,
    });
    setEditId(st.id || st._id);
    setShowForm(true);
    setSaveMsg('');
    setTimeout(() => document.querySelector('.scat-form-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async () => {
    const finalName = form.name === 'Custom Session'
      ? form.customName.trim()
      : form.name;

    if (!finalName)                                          { setSaveMsg('Please select or enter a session name.'); return; }
    if (!form.priceINR || parseInt(form.priceINR) < 1)      { setSaveMsg('A valid offer price (≥ ₹1) is required.'); return; }
    if (form.allowedPlatforms.length === 0)                  { setSaveMsg('Select at least one meeting platform.'); return; }

    const origPrice  = parseInt(form.originalPriceINR) || 0;
    const offerPrice = parseInt(form.priceINR);

    if (origPrice > 0 && origPrice <= offerPrice) {
      setSaveMsg('Regular (slash) price must be greater than the offer price, or leave it blank.'); return;
    }

    setSaving(true); setSaveMsg('');

    const preset  = SESSION_TYPE_PRESETS.find(p => p.name === finalName);
    const payload = {
      name:             finalName,
      icon:             preset?.icon || '⚡',
      description:      form.description.trim(),
      duration:         Number(form.duration),
      topics:           form.topics,
      allowedPlatforms: form.allowedPlatforms,
      originalPriceINR: origPrice,
      priceINR:         offerPrice,
      enabled:          form.enabled,
    };

    try {
      if (isMock) {
        await new Promise(r => setTimeout(r, 500));
        if (editId) {
          persist(sessions.map(s => (s.id === editId || s._id === editId) ? { ...s, ...payload } : s));
        } else {
          persist([...sessions, { id: `st_${Date.now()}`, ...payload }]);
        }
      } else {
        // TODO §BACKEND: POST /v1/mentors/me/session-types
        if (editId) {
          const res = await apiClient.put(`/mentors/me/session-types/${editId}`, payload);
          persist(sessions.map(s => (s.id === editId || s._id === editId) ? (res.sessionType || { ...s, ...payload }) : s));
        } else {
          const res = await apiClient.post('/mentors/me/session-types', payload);
          persist([...sessions, res.sessionType || { id: `st_${Date.now()}`, ...payload }]);
        }
      }
      setShowForm(false);
    } catch (err) {
      setSaveMsg(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Remove this session type? Mentees will no longer be able to book it.')) return;
    try {
      if (!isMock) await apiClient.delete(`/mentors/me/session-types/${id}`);
      persist(sessions.filter(s => s.id !== id && s._id !== id));
    } catch (err) {
      alert(err.message || 'Failed to delete. Please try again.');
    }
  };

  // ── Toggle enabled/hidden ──────────────────────────────────
  const handleToggle = (id) => {
    const updated = sessions.map(s =>
      (s.id === id || s._id === id) ? { ...s, enabled: !s.enabled } : s
    );
    persist(updated);
    if (!isMock) {
      const st = updated.find(s => s.id === id || s._id === id);
      apiClient.put(`/mentors/me/session-types/${id}`, { enabled: st?.enabled }).catch(() => {});
    }
  };

  // ── Toggle platform in form ────────────────────────────────
  const togglePlatform = (pid) => {
    const curr = form.allowedPlatforms;
    if (curr.includes(pid)) {
      if (curr.length === 1) return; // at least one required
      set('allowedPlatforms', curr.filter(p => p !== pid));
    } else {
      set('allowedPlatforms', [...curr, pid]);
    }
  };

  // ── Computed stats ────────────────────────────────────────
  const activeSessions = sessions.filter(s => s.enabled);
  const earningRangeMin = activeSessions.length
    ? Math.min(...activeSessions.map(s => Math.round((s.priceINR || 0) * (1 - COMMISSION_RATE))))
    : 0;
  const earningRangeMax = activeSessions.length
    ? Math.max(...activeSessions.map(s => Math.round((s.priceINR || 0) * (1 - COMMISSION_RATE))))
    : 0;

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
      <div style={{ fontSize: '2rem', marginBottom: 12 }}>⏳</div>
      Loading your session types…
    </div>
  );

  return (
    <div className="scat-root">

      {/* ── Page header ─────────────────────────────────── */}
      <div className="scat-page-header">
        <div>
          <h1 className="db-page-title">🎯 My Services</h1>
          <p className="db-page-sub">Configure your session offerings — set pricing, topics, duration, and allowed meeting platforms.</p>
        </div>
        <button className="btn btn-primary scat-add-btn" onClick={openCreate}>
          + Add Session Type
        </button>
      </div>

      {/* ── Commission policy banner ─────────────────────── */}
      <div className="scat-policy-banner">
        <div className="scat-policy-icon">🏢</div>
        <div className="scat-policy-body">
          <div className="scat-policy-title">Platform Commission Policy</div>
          <div className="scat-policy-text">
            <strong>{PLATFORM_COMPANY}</strong> deducts a{' '}
            <strong>{COMMISSION_RATE * 100}% platform fee</strong> from every booked session to cover
            infrastructure hosting, payment processing, customer support, and environment maintenance.
            You keep <strong>{(1 - COMMISSION_RATE) * 100}% of every session fee</strong>, credited to your wallet within 24 hours of session completion.
          </div>
        </div>
        <div className="scat-policy-stat-box">
          <div className="scat-policy-stat-lbl">Your share</div>
          <div className="scat-policy-stat-val">{(1 - COMMISSION_RATE) * 100}%</div>
          <div className="scat-policy-stat-sub">of every session</div>
        </div>
      </div>

      {/* ── Stats cards ──────────────────────────────────── */}
      <div className="db-stats-grid-4" style={{ marginBottom: 24 }}>
        {[
          { icon: '🎯', label: 'Active Services',      v: activeSessions.length },
          { icon: '💰', label: 'Min Earning / Session', v: activeSessions.length ? convertPrice(earningRangeMin, currency) : '—' },
          { icon: '💎', label: 'Max Earning / Session', v: activeSessions.length ? convertPrice(earningRangeMax, currency) : '—' },
          { icon: '📊', label: 'Platform Commission',   v: `${COMMISSION_RATE * 100}%` },
        ].map(s => (
          <div key={s.label} className="db-stat-card">
            <div className="dsc-icon">{s.icon}</div>
            <div className="dsc-label">{s.label}</div>
            <div className="dsc-value" style={{ fontSize: '1.1rem' }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* ── Create / Edit Form ───────────────────────────── */}
      {showForm && (
        <div className="scat-form-card db-card" style={{ marginBottom: 24 }}>
          <div className="scat-form-header">
            <span>{editId ? '✏️ Edit Session Type' : '➕ Create New Session Type'}</span>
            <button className="scat-close-btn" onClick={() => setShowForm(false)}>✕</button>
          </div>

          <div className="scat-form-body">

            {/* ─ Session type preset picker ─ */}
            <div className="scat-form-section">
              <label className="db-field-label">Session Type *</label>
              <div className="scat-preset-grid">
                {SESSION_TYPE_PRESETS.map(p => (
                  <button
                    key={p.name}
                    type="button"
                    className={`scat-preset-btn ${form.name === p.name ? 'selected' : ''}`}
                    onClick={() => {
                      set('name', p.name);
                      if (p.name !== 'Custom Session' && !form.description.trim()) {
                        set('description', p.description);
                      }
                    }}
                  >
                    <span className="scat-preset-icon">{p.icon}</span>
                    <span className="scat-preset-name">{p.name}</span>
                  </button>
                ))}
              </div>
              {form.name === 'Custom Session' && (
                <input
                  className="db-field"
                  style={{ marginTop: 10 }}
                  placeholder="Enter your custom session name…"
                  value={form.customName}
                  onChange={e => set('customName', e.target.value)}
                />
              )}
            </div>

            {/* ─ Duration + Status ─ */}
            <div className="scat-form-two-col">
              <div>
                <label className="db-field-label">Session Duration</label>
                <select
                  className="db-field"
                  value={form.duration}
                  onChange={e => set('duration', parseInt(e.target.value))}
                >
                  {DURATION_OPTIONS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="db-field-label">Visibility</label>
                <div className="db-toggle-wrap" style={{ paddingTop: 9 }}>
                  <button
                    type="button"
                    className={`db-toggle ${form.enabled ? 'on' : ''}`}
                    onClick={() => set('enabled', !form.enabled)}
                  />
                  <span className="db-toggle-label" style={{ color: form.enabled ? '#16a34a' : '#6b7280' }}>
                    {form.enabled ? '● Active — Visible to mentees' : '○ Hidden — Not bookable'}
                  </span>
                </div>
              </div>
            </div>

            {/* ─ Description ─ */}
            <div>
              <label className="db-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                Description
                <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '.72rem' }}>{form.description.length}/400</span>
              </label>
              <textarea
                className="db-field db-textarea"
                rows={3}
                maxLength={400}
                placeholder="What will mentees get from this session? What should they prepare beforehand?"
                value={form.description}
                onChange={e => set('description', e.target.value)}
              />
            </div>

            {/* ─ Topics ─ */}
            <div>
              <label className="db-field-label">Session Topics / Keywords</label>
              <TopicInput topics={form.topics} onChange={v => set('topics', v)} />
              <p className="scat-field-hint">Press Enter or comma to add each topic. These help mentees find your sessions.</p>
            </div>

            {/* ─ Allowed Platforms ─ */}
            <div>
              <label className="db-field-label">Allowed Meeting Platforms</label>
              <div className="scat-platform-grid">
                {PLATFORM_OPTIONS.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    className={`scat-platform-toggle ${form.allowedPlatforms.includes(p.id) ? 'selected' : ''}`}
                    onClick={() => togglePlatform(p.id)}
                  >
                    <span className="scat-plat-toggle-icon">{p.icon}</span>
                    <span className="scat-plat-toggle-label">{p.label}</span>
                    {form.allowedPlatforms.includes(p.id) && (
                      <span className="scat-plat-check">✓</span>
                    )}
                  </button>
                ))}
              </div>
              <p className="scat-field-hint">At least one platform required. Mentees choose from your selected platforms at booking.</p>
            </div>

            {/* ─ Pricing ─ */}
            <div className="scat-pricing-section">
              <div className="scat-section-title-sm">💰 Pricing Configuration</div>

              <div className="scat-form-two-col">
                <div>
                  <label className="db-field-label">
                    Regular Price (INR)
                    <span className="scat-field-opt"> — optional, shown with strikethrough</span>
                  </label>
                  <div className="scat-price-wrap">
                    <span className="scat-price-sym">₹</span>
                    <input
                      className="db-field scat-price-field"
                      type="number"
                      min={0}
                      placeholder="e.g. 2000"
                      value={form.originalPriceINR}
                      onChange={e => set('originalPriceINR', e.target.value)}
                    />
                  </div>
                  <p className="scat-field-hint">The "was" price. Set this to show an offer discount to mentees.</p>
                </div>

                <div>
                  <label className="db-field-label">
                    Offer Price (INR) <span style={{ color: '#ef4444' }}>*</span>
                    <span className="scat-field-opt"> — actual price charged</span>
                  </label>
                  <div className="scat-price-wrap">
                    <span className="scat-price-sym">₹</span>
                    <input
                      className="db-field scat-price-field"
                      type="number"
                      min={1}
                      placeholder="e.g. 1500"
                      value={form.priceINR}
                      onChange={e => set('priceINR', e.target.value)}
                    />
                  </div>
                  <p className="scat-field-hint">The price mentees pay. Commission is calculated on this amount.</p>
                </div>
              </div>

              {/* Live commission preview */}
              {!!parseInt(form.priceINR) && (
                <CommissionPreview
                  priceINR={form.priceINR}
                  originalPriceINR={form.originalPriceINR}
                  currency={currency}
                />
              )}
            </div>

            {/* Error */}
            {saveMsg && (
              <div className="scat-form-error">⚠ {saveMsg}</div>
            )}

            {/* Actions */}
            <div className="scat-form-actions">
              <button
                className="btn btn-primary"
                style={{ minWidth: 180 }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '⏳ Saving…' : editId ? '💾 Save Changes' : '🚀 Create Session Type'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowForm(false)} disabled={saving}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── Session type cards ───────────────────────────── */}
      {sessions.length === 0 ? (
        <div className="db-empty">
          <div className="db-empty-ico">🎯</div>
          <h3>No session types configured yet</h3>
          <p>Create your first session type so mentees can discover and book you.</p>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={openCreate}>
            + Create Your First Session Type
          </button>
        </div>
      ) : (
        <>
          {activeSessions.length > 0 && (
            <div className="scat-section-lbl scat-section-active">
              ● Active — Visible to Mentees ({activeSessions.length})
            </div>
          )}
          <div className="scat-cards-grid">
            {activeSessions.map(st => (
              <SessionTypeCard
                key={st.id || st._id}
                st={st}
                onEdit={openEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
                currency={currency}
              />
            ))}
          </div>

          {sessions.filter(s => !s.enabled).length > 0 && (
            <>
              <div className="scat-section-lbl scat-section-hidden">
                ○ Hidden — Not Bookable ({sessions.filter(s => !s.enabled).length})
              </div>
              <div className="scat-cards-grid">
                {sessions.filter(s => !s.enabled).map(st => (
                  <SessionTypeCard
                    key={st.id || st._id}
                    st={st}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    currency={currency}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
